import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const BARS = 30;

const VoiceIntro = ({ src, lang = "de", stop = false }) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const phasesRef = useRef(Array.from({ length: BARS }, () => Math.random() * Math.PI * 2));
  const playingRef = useRef(false);
  const manualOffRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  // ---- decorative waveform ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let t = 0;
    const render = () => {
      rafRef.current = requestAnimationFrame(render);
      t += 0.05;
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      const gap = 2;
      const barW = (width - gap * (BARS - 1)) / BARS;
      for (let i = 0; i < BARS; i++) {
        const phase = phasesRef.current[i];
        const active = playingRef.current;
        const wave = Math.abs(Math.sin(t * 1.6 + phase));
        const amp = active ? 0.22 + 0.78 * wave * (0.6 + 0.4 * Math.sin(t * 0.7 + i)) : 0.08;
        const h = Math.max(3, amp * height);
        const x = i * (barW + gap);
        const y = (height - h) / 2;
        const grad = ctx.createLinearGradient(0, y, 0, y + h);
        grad.addColorStop(0, active ? "rgba(255,0,51,0.95)" : "rgba(255,0,51,0.35)");
        grad.addColorStop(1, active ? "rgba(255,80,110,0.65)" : "rgba(255,0,51,0.15)");
        ctx.fillStyle = grad;
        const r = Math.min(barW / 2, 2);
        ctx.beginPath();
        ctx.roundRect(x, y, barW, h, r);
        ctx.fill();
      }
    };
    render();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const setPlay = (v) => { playingRef.current = v; setPlaying(v); };

  const play = async () => {
    try {
      await audioRef.current.play();
      setPlay(true);
    } catch {
      setPlay(false);
    }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      manualOffRef.current = true;
      setPlay(false);
    } else {
      manualOffRef.current = false;
      play();
    }
  };

  // autoplay attempt + start on first interaction if blocked
  useEffect(() => {
    play();
    const onFirst = () => {
      if (!playingRef.current && !manualOffRef.current) play();
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
    };
    window.addEventListener("pointerdown", onFirst);
    window.addEventListener("keydown", onFirst);
    return () => {
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload when language/src changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.load();
    if (!manualOffRef.current) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // stop when trailer video plays
  useEffect(() => {
    if (stop && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlay(false);
    }
  }, [stop]);

  return (
    <div
      className="inline-flex items-center gap-4 border border-white/15 bg-[#0F0F0F]/70 backdrop-blur-md px-4 py-3"
      data-testid="voice-intro"
    >
      <audio ref={audioRef} src={src} preload="auto" onEnded={() => setPlay(false)} data-testid="voice-intro-audio" />
      <button
        onClick={toggle}
        data-testid="voice-intro-toggle"
        aria-label={playing ? (lang === "de" ? "Ton aus" : "Sound off") : (lang === "de" ? "Ton an" : "Sound on")}
        className={`relative shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
          playing
            ? "bg-[#ff0033] text-white shadow-[0_0_24px_rgba(255,0,51,0.6)]"
            : "bg-white/10 text-white hover:bg-white/20"
        }`}
      >
        {playing ? <Volume2 size={20} /> : <VolumeX size={20} />}
      </button>
      <div className="flex flex-col">
        <span className="font-mono-lab text-[10px] tracking-[0.2em] uppercase text-neutral-400">
          {lang === "de" ? "Intro anhören" : "Listen to intro"}
        </span>
        <canvas ref={canvasRef} width={220} height={36} className="mt-1.5 w-[180px] md:w-[220px] h-9" />
      </div>
    </div>
  );
};

export default VoiceIntro;
